import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  random,
} from 'remotion';

// #300 Pixelate Transition - 像素化方块转场
export const PixelateTransition: React.FC<{
  text?: string;
  gridSize?: number;
  color1?: string;
  color2?: string;
  backgroundColor?: string;
}> = ({
  text = 'PIXEL',
  gridSize = 20,
  color1 = '#ff0080',
  color2 = '#00ffff',
  backgroundColor = '#0a0010',
}) => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();

  const cellWidth = width / gridSize;
  const cellHeight = height / gridSize;

  // 中心文字动画
  const textOpacity = spring({
    frame: frame - 60,
    fps,
    config: { damping: 200 },
  });

  const textScale = spring({
    frame: frame - 60,
    fps,
    config: { damping: 15, stiffness: 100 },
  });

  return (
    <AbsoluteFill style={{ backgroundColor, justifyContent: 'center', alignItems: 'center' }}>
      {/* 像素网格 */}
      {Array.from({ length: gridSize * gridSize }).map((_, i) => {
        const x = i % gridSize;
        const y = Math.floor(i / gridSize);

        // 随机延迟
        const delay = random(`pixel-${i}`) * 40;

        const progress = interpolate(
          frame,
          [delay, delay + 30],
          [0, 1],
          { extrapolateRight: 'clamp' }
        );

        const opacity = progress;
        const scale = progress;

        // 颜色渐变
        const colorProgress = (x + y) / (gridSize * 2);
        const color = interpolate(colorProgress, [0, 1], [0, 1]);

        return (
          <div
            key={`pixel-${i}`}
            style={{
              position: 'absolute',
              left: x * cellWidth,
              top: y * cellHeight,
              width: cellWidth,
              height: cellHeight,
              background: `linear-gradient(135deg, ${color1}, ${color2})`,
              opacity: opacity * 0.3,
              transform: `scale(${scale})`,
              transformOrigin: 'center',
            }}
          />
        );
      })}

      {/* 中心文字 */}
      <div
        style={{
          fontSize: 120,
          fontWeight: 'bold',
          background: `linear-gradient(135deg, ${color1}, ${color2})`,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          color: 'transparent',
          opacity: textOpacity,
          transform: `scale(${textScale})`,
          textShadow: `0 0 40px ${color1}80`,
          zIndex: 10,
        }}
      >
        {text}
      </div>
    </AbsoluteFill>
  );
};

// #301 Collage Transition - 多格缩放滑动效果
export const CollageTransition: React.FC<{
  text?: string;
  rows?: number;
  cols?: number;
  color1?: string;
  color2?: string;
  backgroundColor?: string;
}> = ({
  text = 'COLLAGE',
  rows = 4,
  cols = 4,
  color1 = '#f59e0b',
  color2 = '#06b6d4',
  backgroundColor = '#0f0f1e',
}) => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();

  const cellWidth = width / cols;
  const cellHeight = height / rows;

  // 中心文字动画
  const textOpacity = spring({
    frame: frame - 80,
    fps,
    config: { damping: 200 },
  });

  const textScale = spring({
    frame: frame - 80,
    fps,
    config: { damping: 15, stiffness: 100 },
  });

  return (
    <AbsoluteFill style={{ backgroundColor, justifyContent: 'center', alignItems: 'center' }}>
      {/* 多格拼贴 */}
      {Array.from({ length: rows * cols }).map((_, i) => {
        const row = Math.floor(i / cols);
        const col = i % cols;

        // 随机延迟
        const delay = random(`collage-${i}`) * 50;

        const progress = spring({
          frame: frame - delay,
          fps,
          config: { damping: 15, stiffness: 100 },
        });

        const translateX = interpolate(
          random(`collage-x-${i}`),
          [0, 1],
          [-width / 2, width / 2]
        );

        const translateY = interpolate(
          random(`collage-y-${i}`),
          [0, 1],
          [-height / 2, height / 2]
        );

        const rotation = random(`collage-rotation-${i}`) * 360;

        // 颜色渐变
        const colorProgress = (row + col) / (rows + cols);

        return (
          <div
            key={`collage-${i}`}
            style={{
              position: 'absolute',
              left: col * cellWidth,
              top: row * cellHeight,
              width: cellWidth,
              height: cellHeight,
              background: `linear-gradient(135deg, ${color1}, ${color2})`,
              opacity: progress,
              transform: `
                translate(${translateX * (1 - progress)}px, ${translateY * (1 - progress)}px)
                rotate(${rotation * (1 - progress)}deg)
                scale(${0.5 + progress * 0.5})
              `,
              boxShadow: `0 0 20px ${color1}40`,
              border: '2px solid rgba(255, 255, 255, 0.1)',
            }}
          />
        );
      })}

      {/* 中心文字 */}
      <div
        style={{
          fontSize: 120,
          fontWeight: 'bold',
          background: `linear-gradient(135deg, ${color1}, ${color2})`,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          color: 'transparent',
          opacity: textOpacity,
          transform: `scale(${textScale})`,
          textShadow: `0 0 40px ${color1}80`,
          zIndex: 10,
        }}
      >
        {text}
      </div>
    </AbsoluteFill>
  );
};
